import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce } from 'lodash';
import { checkUsernameAvailability } from '../schema'; // Adjust path as needed
// import { debounce } from 'lodash';

import {
  addGroupOption,
  getAdminDetailsStart,
  getAdminRolesStart,
  getAdminsListStart,
  getStaffGroupsStart,
  setSelectedGroup,
  togglePasswordVisibility,
} from "../../../../store/redux-slices/staffSlice";
import { toast } from "../../../../components/Toast";
import { ROLES } from "../constants";
import { createStaffSchema } from '../schema';

const useStaffForm = ({
  group,
  role,
  adminId,
  isEdit = false,
  values,
  setFieldValue,
  setFieldError,
  touched,
  setFieldTouched,
  validateField,
  // staffDetails
}) => {

  let { adminRole: adminRoles } = useSelector((state) => state.login);

  adminRoles = adminRoles?.filter((role) => role.adminRoleId !== 1);

  const { adminDetails } = useSelector((state) => state.admins);
  const { adminDetails: staffDetails } = useSelector((state) => state.staff);

  const {
    adminsList,
    groupOptions,
    loading,
    passwordVisible,
    selectedGroup,
    error,
    resetStaffForm
  } = useSelector((state) => state.staff || {});


  const dispatch = useDispatch();
  const navigate = useNavigate();




  // Add username checking states
  const [usernameStatus, setUsernameStatus] = useState({
    isChecking: false,
    isAvailable: null,
    message: '',
    lastCheckedUsername: ''
  });

  // Add ref to track the current request
  const currentUsernameRequest = useRef(null);

  // Clear username status
  const clearUsernameStatus = useCallback(() => {
    console.log('Clearing username status');
    setUsernameStatus({
      isChecking: false,
      isAvailable: null,
      message: '',
      lastCheckedUsername: ''
    });

    // Clear any field errors for username
    if (setFieldError) {
      setFieldError('adminUsername', '');
    }
  }, [setFieldError]);

  // NON-debounced username check function
  const performUsernameCheck = async (username, currentUserId = null) => {
    console.log('performUsernameCheck called for:', username);

    // Cancel previous request if it exists
    if (currentUsernameRequest.current) {
      currentUsernameRequest.current.cancelled = true;
    }

    if (!username || username.trim().length < 8) {
      clearUsernameStatus();
      return;
    }

    const trimmedUsername = username.trim();

    // For edit mode, if username hasn't changed from original, show current username
    if (isEdit && staffDetails?.adminUsername === trimmedUsername) {
      console.log('Username is same as original, showing current username');
      setUsernameStatus({
        isChecking: false,
        isAvailable: true,
        message: 'Current username',
        lastCheckedUsername: trimmedUsername
      });

      // Clear any existing errors
      if (setFieldError) {
        setFieldError('adminUsername', '');
      }
      return;
    }

    console.log('Starting API check for username:', trimmedUsername);
    setUsernameStatus(prev => ({
      ...prev,
      isChecking: true,
      message: 'Checking username availability...',
      lastCheckedUsername: trimmedUsername
    }));

    // Create a new request object
    const requestObj = { cancelled: false };
    currentUsernameRequest.current = requestObj;

    try {
      const response = await checkUsernameAvailability(trimmedUsername, currentUserId);

      // Check if this request was cancelled
      if (requestObj.cancelled) {
        console.log('Request was cancelled, ignoring response');
        return;
      }

      console.log('Username check response:', response);

      if (response.available) {
        setUsernameStatus({
          isChecking: false,
          isAvailable: true,
          message: response.message || 'Username is available!',
          lastCheckedUsername: trimmedUsername
        });

        // Clear any existing errors
        if (setFieldError) {
          setFieldError('adminUsername', '');
        }
      } else {
        setUsernameStatus({
          isChecking: false,
          isAvailable: false,
          message: response.message || 'Username already exists',
          lastCheckedUsername: trimmedUsername
        });

        // Set field error
        if (setFieldError) {
          setFieldError('adminUsername', response.message || 'Username already exists');
        }
      }
    } catch (error) {
      // Check if this request was cancelled
      if (requestObj.cancelled) {
        console.log('Request was cancelled, ignoring error');
        return;
      }

      console.error('Username check error:', error);
      setUsernameStatus({
        isChecking: false,
        isAvailable: null,
        message: 'Error checking username availability',
        lastCheckedUsername: trimmedUsername
      });
    } finally {
      // Clear the current request reference if this was the current request
      if (currentUsernameRequest.current === requestObj) {
        currentUsernameRequest.current = null;
      }
    }
  };

  // DEBOUNCED version of the username check (ONLY debounce here)
  const debouncedUsernameCheck = useCallback(
    debounce((username, currentUserId = null) => {
      console.log('Debounced function called for username:', username);
      performUsernameCheck(username, currentUserId);
    }, 500), // 500ms debounce delay
    [isEdit, staffDetails?.adminUsername, setFieldError]
  );

  // Function to handle username change
  const handleUsernameChange = (e, currentUserId = null) => {
    const { value } = e.target;

    console.log('handleUsernameChange called with value:', value);

    // If the input is empty, clear everything immediately
    if (!value || value.trim() === '') {
      clearUsernameStatus();
      return;
    }

    // If username is too short, clear status but don't show error
    if (value.trim().length < 3) {
      setUsernameStatus({
        isChecking: false,
        isAvailable: null,
        message: '',
        lastCheckedUsername: ''
      });

      // Clear any existing errors
      if (setFieldError) {
        setFieldError('adminUsername', '');
      }
      return;
    }

    // Use the debounced function
    debouncedUsernameCheck(value, currentUserId);
  };

  // Cleanup function to cancel any pending requests
  const cleanupUsernameCheck = useCallback(() => {
    if (currentUsernameRequest.current) {
      currentUsernameRequest.current.cancelled = true;
      currentUsernameRequest.current = null;
    }
    debouncedUsernameCheck.cancel(); // Cancel the debounced function
  }, [debouncedUsernameCheck]);















  // Initialize form data
  useEffect(() => {
    if (group && !selectedGroup) {
      dispatch(setSelectedGroup({ label: group, value: group }));
      if (setFieldValue) {
        setFieldValue("group", group);
      }
    }
  }, [group, selectedGroup, dispatch, setFieldValue]);

  // Fetch initial data
  useEffect(() => {
    dispatch(getAdminRolesStart());
    dispatch(getStaffGroupsStart());
  }, []);

  useEffect(() => {
    if (["Staff", "Manager"].includes(values?.role)) {
      dispatch(
        getAdminsListStart({
          roleId: ROLES.find((role) => role.title === values?.role)?.roleId,
        })
      );
    }
  }, [values?.role, dispatch]);

  // Fetch admin details when needed
  useEffect(() => {
    if (!isEdit) {
      return;
    }

    if (
      (values?.role === "Staff" && values?.adminId) ||
      (values?.role === "Manager" && values?.adminId)
    ) {
      dispatch(getAdminDetailsStart({
        adminUserId: staffDetails?.adminUserId
      }));
    }
  }, [values?.role, values?.adminId]);

  useEffect(() => {
    if (
      ((values?.role === "Staff" && values?.adminId) ||
        (values?.role === "Manager" && values?.adminId)) && !isEdit
    ) {
      dispatch(getAdminDetailsStart({ adminUserId: values?.adminId }));
    }
  }, [values?.adminId]);

  // Custom handlers
  const groupChangeHandler = (option) => {
    if (option === null) {
      dispatch(setSelectedGroup(null));
      if (setFieldValue) {
        setFieldValue("group", "");
      }
    } else {
      dispatch(setSelectedGroup({ label: option.label, value: option.value }));
      if (setFieldValue) {
        setFieldValue("group", option.value);
      }
    }
  };

  const handleCreateGroup = async (inputValue) => {
    // Use Yup validation for the group field
    try {
      await createStaffSchema.fields.group.validate(inputValue);
    } catch (err) {
      if (setFieldValue) {
        setFieldValue('group', inputValue, false); // set value but don't validate immediately
      }
      if (typeof setFieldError === 'function') {
        setFieldError('group', err.message);
      }
      return;
    }
    if (groupOptions && groupOptions.some(opt => opt.value.toLowerCase() === inputValue.trim().toLowerCase())) {
      if (typeof setFieldError === 'function') {
        setFieldError('group', 'Group already exists');
      }
      return;
    }
    const newOption = { label: inputValue.trim(), value: inputValue.trim() };
    dispatch(addGroupOption(newOption));
    dispatch(setSelectedGroup(newOption));
    if (setFieldValue) {
      setFieldValue('group', inputValue.trim());
    }
    if (typeof setFieldError === 'function') {
      setFieldError('group', undefined);
    }
  };

  const roleChangeHandler = (e) => {
    const newRole = e.target.value;
    setFieldValue('role', newRole);
    setFieldValue('adminId', '');
    setFieldValue('permission', {});

    if (['Staff', 'Manager'].includes(newRole)) {
      setFieldError('adminId', undefined);
      setFieldTouched('adminId', false, false);
    }
  };

  const permissionHandler = (e, key) => {
    if (!setFieldValue || !values) return;

    const { value, checked } = e.target;
    const currentPermissions = values.permission?.[key] || [];

    if (value === "R" || value === "DR" || currentPermissions.includes("R")) {
      if (value === "R" && !checked) {
        const newPermissions = { ...values.permission };
        delete newPermissions[key];
        setFieldValue("permission", newPermissions);
      } else {
        const newPermissions = checked
          ? [...currentPermissions, value]
          : currentPermissions.filter((p) => p !== value);

        setFieldValue("permission", {
          ...values.permission,
          [key]: newPermissions.length > 0 ? newPermissions : undefined,
        });
      }
    } else {
      toast("Please select Read permission first", "error");
    }
  };

  const togglePassword = () => {
    dispatch(togglePasswordVisibility());
  };




  return {
    // Custom handlers
    groupChangeHandler,
    handleCreateGroup,
    roleChangeHandler,
    permissionHandler,
    togglePassword,

    // Data
    adminRoles,
    adminsList: adminsList || [],
    adminDetails: values?.role === 'Admin' ? adminDetails : staffDetails,
    groupOptions: groupOptions || [],
    selectedGroup,
    resetStaffForm,
    managrialPermissions: values?.role === 'Admin' ? adminDetails : staffDetails,
    // State
    passwordType: passwordVisible ? "text" : "password",
    apiError: error,

    // Utils
    navigate,


    usernameStatus,
    handleUsernameChange,
    clearUsernameStatus,
    cleanupUsernameCheck

  };
};

export default useStaffForm;
